import { WarningDialog } from '@strata/react-dialog';
import { FormContainer } from '@strata/react-form-container';
import { TextAreaFormik, TextInputFormik } from '@strata/react-inputs';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { dialogStrings, validationStrings } from '@strata/strata-ui-strings';
import { Formik } from 'formik';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import * as Yup from 'yup';

import { Table } from 'components/Table';
import { CREATE_ROUTE } from 'services/constants';
import { handleError } from 'services/ErrorManagement';
import { post, put, useGet } from 'services/Http';
import { formatCodesForDisplay, packageServiceOidForHttp } from 'services/OidSupport';

const formatIsBrand = (isBrand) => {
    if (isBrand == null) {
        return null;
    }
    return isBrand ? 'Yes' : 'No';
};

const getValidationSchema = () =>
    Yup.object({
        oidName: Yup.string()
            .max(50, validationStrings.maxCharacter[50])
            .required(validationStrings.requiredField.errorMessage),
        description: Yup.string()
            .max(50, validationStrings.maxCharacter[50])
            .required(validationStrings.requiredField.errorMessage),
        genericProductDescription: Yup.string()
            .max(50, validationStrings.maxCharacter[50])
            .required(validationStrings.requiredField.errorMessage),
        brandProductDescription: Yup.string()
            .max(50, validationStrings.maxCharacter[50])
            .required(validationStrings.requiredField.errorMessage),
        gpi: Yup.string().max(14, validationStrings.maxCharacter[14]),
    });

const getParentProductsList = (parentProducts) =>
    (parentProducts ?? []).map((parent) => ({
        name: parent.display_name,
        secondaryData: parent.parent_product_service,
    }));

export const ProductServiceDetails = () => {
    const { productServiceOid } = useParams();
    const navigate = useNavigate();

    const goHome = () => navigate('/product-service');

    const isCreate = productServiceOid === CREATE_ROUTE;

    const productService = useGet(
        !isCreate ? `/reference/productservices/${productServiceOid}` : null,
    );
    const parentProductsList = getParentProductsList(useGet('/reference/parent_product_services'));

    const [showWarning, setShowWarning] = useState(false);

    return (
        <Formik
            initialValues={{
                oidName: productService?.oid || '',
                description: productService?.description || '',
                genericProductDescription: productService?.generic_product_description || '',
                brandProductDescription: productService?.brand_product_description || '',
                gpi: productService?.gpi || '',
                intake: productService?.intake || false,
                parentProductService: parentProductsList.find(
                    (parent) => parent.secondaryData === productService?.parent_product_service,
                ),
                codes: formatCodesForDisplay(productService?.codes ?? []),
            }}
            enableReinitialize
            validationSchema={getValidationSchema()}
            onSubmit={async (values) => {
                try {
                    const body = packageServiceOidForHttp({
                        oid: values.oidName,
                        description: values.description,
                        genericProductDescription: values.genericProductDescription,
                        brandProductDescription: values.brandProductDescription,
                        parentProductService: values.parentProductService?.secondaryData,
                        gpi: values.gpi,
                        intake: values.intake,
                        codeValues: values.codes,
                    });
                    if (isCreate) {
                        await post('/reference/productservices/', body);
                    } else {
                        await put(`/reference/productservices/${productService.oid}`, body);
                    }
                    goHome();
                } catch (err) {
                    handleError('Error saving product service.', err);
                }
            }}
        >
            {(formik) => (
                <>
                    <WarningDialog
                        dialogTitle={dialogStrings.unsavedChanges.title}
                        isOpen={showWarning}
                        onSecondaryButtonClick={() => setShowWarning(false)}
                        onPrimaryButtonClick={goHome}
                        dialogContent={<p>{dialogStrings.unsavedChanges.body}</p>}
                    />
                    <FormContainer
                        formTitle={`${isCreate ? 'Create' : 'Edit'} Product Service`}
                        isLoading={!isCreate && !productService}
                        onFormSubmit={formik.handleSubmit}
                        isSubmitting={formik.isSubmitting || !(formik.dirty && formik.isValid)}
                        onSecondaryButtonClick={() => {
                            if (formik.dirty) {
                                setShowWarning(true);
                            } else {
                                goHome();
                            }
                        }}
                        formLeftContent={
                            <>
                                <TextInputFormik
                                    label="Product Service Name"
                                    id="oidName"
                                    required
                                    disabled={!isCreate}
                                />
                                <TextInputFormik
                                    label="Full Display Name"
                                    id="description"
                                    required
                                />
                                <TextInputFormik
                                    label="Brand Display Name"
                                    id="brandProductDescription"
                                    required
                                />
                                <TextInputFormik
                                    label="Generic Display Name"
                                    id="genericProductDescription"
                                    required
                                    helperText="Enter the brand display name if no generic is available"
                                />
                                <SuggestSingleSelectFormik
                                    label="Parent Product"
                                    id="parentProductService"
                                    values={parentProductsList}
                                />
                                <TextInputFormik label="Generic Product Identifier" id="gpi" />
                                <SuggestSingleSelectFormik
                                    id="intake"
                                    label="Intake?"
                                    required
                                    values={['Yes', 'No']}
                                    disabled={!isCreate}
                                    helperText="Can this Product/Service be used for Intake?"
                                    onChange={(newValue) =>
                                        formik.setFieldValue('intake', newValue === 'Yes')
                                    }
                                    selectedItem={{ name: formik.values.intake ? 'Yes' : 'No' }}
                                />
                                <TextAreaFormik
                                    id="codes"
                                    label="Service Codes"
                                    helperText="Comma delimited service code list."
                                    fill
                                />
                                {productService?.codes && (
                                    <Table
                                        columnNames={[
                                            'Code',
                                            'Display Name',
                                            'Dosage',
                                            'Strength',
                                            'Brand?',
                                        ]}
                                        columnWidths={[undefined, 300, 200, 200, 100]}
                                        tableData={productService.codes.map((code) => [
                                            code.code,
                                            code.description,
                                            code.dosage,
                                            code.strength
                                                ? `${code.strength} ${code.strength_unit_of_measure}`
                                                : null,
                                            formatIsBrand(code.is_brand),
                                        ])}
                                    />
                                )}
                            </>
                        }
                    />
                </>
            )}
        </Formik>
    );
};
